import type { HelpStep } from "newui/components/ui/Help";

/**
 * Guided tour steps for "Allocate roles to several users" (PRO-730 topic
 * chooser).
 *
 * D10 rewrite — the entry point now opens a user picker first, THEN hands
 * off to the same Add flow a single user's "Manage roles" -> Add button
 * uses, rather than one combined wizard. The tour follows that same path:
 *   1. Highlight "Allocate Roles" -> open the user picker.
 *   2. The picker as a whole (search/select users).
 *   3. Its "Next" control -> synthesise the hand-off into the Add flow (a
 *      real click needs >=1 user selected, which the tour never does — see
 *      `advanceToAddAccess`).
 *   4. The Add flow's mode step (role + access level).
 *   5. Its "Next" control -> synthesise the advance straight to the confirm
 *      step (real advance needs a real role pick — see
 *      `advanceAddAccessToConfirm`).
 *   6. "Confirm" -> close.
 *
 * The picker opens via the real `BulkUserPickerModal` (no dummy user needed
 * — it only reads the company's real user list); the Add flow past that
 * point opens against the dummy `TOUR_USER_ASSIGNMENT`, whose negative id
 * both `ManageRolesModal` and `AddAccessFlow` independently refuse to fire a
 * real mutation for (PRO-730).
 */
export interface AllocateRolesSteps {
	advanceToAddAccess: () => void;
	advanceAddAccessToConfirm: () => void;
	closeAllocateRoles: () => void;
	openAllocateRoles: () => void;
}

export const buildAllocateRolesSteps = ({
	openAllocateRoles,
	advanceToAddAccess,
	advanceAddAccessToConfirm,
	closeAllocateRoles,
}: AllocateRolesSteps): HelpStep[] => [
	{
		target: '[data-testid="open-assign-wizard"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Allocate roles to several users</h3>
				<p>
					Use this to assign the same role to many users at once. We&apos;ll open the
					user picker for you on the next step.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
		onAfterStep: () => {
			openAllocateRoles();
		},
	},
	{
		target: '[data-testid="bulk-user-picker-modal"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Pick users</h3>
				<p>Search and select as many users as you like.</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		spotlightPadding: 12,
	},
	{
		target: '[data-testid="bulk-user-picker-next"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Next</h3>
				<p>Once you&apos;re happy with your selection, click Next to choose a role.</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			advanceToAddAccess();
		},
	},
	{
		target: '[data-testid="add-access-flow"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Pick a role and access level</h3>
				<p>
					Choose the role to apply to everyone you selected, then Company Wide or
					Specific access.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="wizard-next"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Move on</h3>
				<p>Company Wide takes you straight to a confirmation screen.</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			advanceAddAccessToConfirm();
		},
	},
	{
		target: '[data-testid="wizard-confirm"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Confirm the assignment</h3>
				<p>
					Review your picks and click Confirm to apply the role to every selected
					user.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			closeAllocateRoles();
		},
	},
];
